import type { SchemaVariant } from "@/types/api";
import { schemaEditorId } from "./stagingModel";

// The schema-variants picker's decision logic (TTTL-179 gap 4, ported from
// old /studio's SchemaVariantBar): introspecting a heterogeneous source
// yields several document shapes; the bar above the input editor picks
// which shape fills the sample document and the auto-seeded schema draft.
// Pure helpers here, wiring in editorContents/pipelineStaging, UI in
// InputVariantBar.

/** The picker's trigger and row text (Figma 16421-178261 / 16421-180155):
 *  the variant's number with its share; the sample count rides as a badge
 *  on the row (variantSamplesBadge). */
export function variantLabel(index: number, v: SchemaVariant): string {
  return `Variant ${index + 1} (${v.share_percent.toFixed(1)}%)`;
}

/** The row's badge: how many sampled documents had the variant's shape. */
export function variantSamplesBadge(v: SchemaVariant): string {
  return `${v.sample_count} ${v.sample_count === 1 ? "sample" : "samples"}`;
}

/** A part of the workspace a variant switch would overwrite. */
export type VariantEditTarget = "input document" | "schema draft";

// Whether the slot holds the user's own schema rather than an applied
// variant: a non-empty draft that isn't the last applied variant text —
// a hand-written draft (null baseline) or a seeded one edited away.
export function schemaIsCustom(
  draft: string | undefined,
  baseline: string | null,
): boolean {
  if ((draft ?? "").trim() === "") return false;
  return draft !== baseline;
}

// Which parts the user edited since the last variant was applied — a
// non-empty result routes the switch through the confirm modal. A null
// input origin means no sample was ever filled (typed-from-scratch input
// is the loadSample guard's business, not ours).
export function variantSwitchEdits(state: {
  inputDocument: string;
  inputOrigin: string | null;
  schemaDraft: string | undefined;
  schemaBaseline: string | null;
}): VariantEditTarget[] {
  const edits: VariantEditTarget[] = [];
  if (state.inputOrigin !== null && state.inputDocument !== state.inputOrigin) {
    edits.push("input document");
  }
  if (schemaIsCustom(state.schemaDraft, state.schemaBaseline)) {
    edits.push("schema draft");
  }
  return edits;
}

// What a dropped source does to its SCHEMA slot: seed the introspected
// majority shape into an empty slot, ask before replacing a custom draft,
// and skip silently when a staged schema already carries the collection's
// name (already covered).
export function seedDecision(state: {
  existingDraft: string | undefined;
  stagedSchemaNames: string[];
  collectionName: string;
}): "seed" | "ask" | "skip" {
  if (state.stagedSchemaNames.includes(state.collectionName)) return "skip";
  return (state.existingDraft ?? "").trim() === "" ? "seed" : "ask";
}

/** The seeded draft body — pretty-printed like old /studio's applyVariant. */
export function schemaDraftText(v: SchemaVariant): string {
  return JSON.stringify(v.schema, null, 2);
}

/** The footer trigger's text: the active variant, or "Custom variant"
 *  while the slot's draft diverged from every applied one. */
export function variantTriggerLabel(
  index: number,
  v: SchemaVariant,
  custom: boolean,
): string {
  return custom ? "Custom variant" : variantLabel(index, v);
}

/** The staged source's introspection result driving the variant bar. */
export interface SourceVariantsState {
  resourceId: string;
  variants: SchemaVariant[];
  activeIndex: number;
}

// Folds a finished introspection into the bar's state: a fresh source
// starts on the majority shape, a re-selected one keeps the user's pick,
// and an un-introspectable result clears the bar.
export function nextSourceVariants(
  prev: SourceVariantsState | null,
  resourceId: string,
  variants: SchemaVariant[] | null,
): SourceVariantsState | null {
  if (!variants || variants.length === 0) return null;
  const activeIndex = prev?.resourceId === resourceId ? prev.activeIndex : 0;
  return { resourceId, variants, activeIndex };
}

// The footer picker renders only while the schema editor shows the seeded
// source draft's slot row (block rows select as "<block>|<slot>"
// composites), for an editable source that clusters into more than one
// shape — view-only studios, single-shape sources and unrelated schema
// rows hide it.
export function variantPickerHidden(
  editsLocked: boolean,
  state: SourceVariantsState | null,
  effectiveSelectedId: string | null,
): boolean {
  if (editsLocked) return true;
  if ((state?.variants.length ?? 0) <= 1) return true;
  const rowId = schemaEditorId(state?.resourceId ?? "");
  return !slotRowSelected(effectiveSelectedId, rowId);
}

// The slot's own editor row, or a block-composite selection of it.
function slotRowSelected(selectedId: string | null, rowId: string): boolean {
  if (selectedId === null) return false;
  return selectedId === rowId || selectedId.endsWith(`|${rowId}`);
}

/** A pick lands only when it changes something: a different variant, or
 *  ANY variant while the draft is custom — re-applying the nominally
 *  active one is then a real request to replace the custom schema. */
export function pickIgnored(
  index: number,
  activeIndex: number,
  custom: boolean,
): boolean {
  return !custom && index === activeIndex;
}
